import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Button,
  IconButton,
  TextField,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { normalizeFromEndpoint } from '../../../utils/endpointUtils';

const ChatConfig = ({
  config,
  onConfigChange,
  communicationEndpoints,
  onEndpointChange,
  onDelete,
}) => {
  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    onConfigChange({
      ...config,
      [field]: value,
    });
  };

  const handleEndpointChange = (event) => {
    const normalizedEndpoint = normalizeFromEndpoint(event.target.value);
    onEndpointChange(normalizedEndpoint);
  };

  const handleQuickReplyDelete = (index) => {
    const newQuickReplies = [...config.quick_replies];
    newQuickReplies.splice(index, 1);
    onConfigChange({
      ...config,
      quick_replies: newQuickReplies,
    });
  };

  const handleQuickReplyAdd = () => {
    const newQuickReplies = [...(config.quick_replies || []), { text: '', value: '' }];
    onConfigChange({
      ...config,
      quick_replies: newQuickReplies,
    });
  };

  const handleQuickReplyChange = (index, field) => (event) => {
    const newQuickReplies = [...config.quick_replies];
    newQuickReplies[index] = {
      ...newQuickReplies[index],
      [field]: event.target.value,
    };
    onConfigChange({
      ...config,
      quick_replies: newQuickReplies,
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Chat Configuration
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel>Platform</InputLabel>
        <Select
          value={config.platform || ''}
          onChange={handleConfigChange('platform')}
          label="Platform"
        >
          <MenuItem value="whatsapp">WhatsApp</MenuItem>
          <MenuItem value="messenger">Facebook Messenger</MenuItem>
          <MenuItem value="telegram">Telegram</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>From Account</InputLabel>
        <Select
          value={config.from_endpoint || ''}
          onChange={handleEndpointChange}
          label="From Account"
        >
          {communicationEndpoints
            .filter(endpoint => endpoint.type === 'chat')
            .map(endpoint => (
              <MenuItem key={endpoint.id} value={endpoint.id}>
                {endpoint.label}
              </MenuItem>
            ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <InputLabel>Priority</InputLabel>
        <Select
          value={config.priority || 'normal'}
          onChange={handleConfigChange('priority')}
          label="Priority"
        >
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="normal">Normal</MenuItem>
          <MenuItem value="low">Low</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mt: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={config.track_read ?? true}
              onChange={handleConfigChange('track_read')}
            />
          }
          label="Track Read Status"
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Replies
        </Typography>
        {config.quick_replies && config.quick_replies.map((reply, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              label="Text"
              value={reply.text}
              onChange={handleQuickReplyChange(index, 'text')}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Value"
              value={reply.value}
              onChange={handleQuickReplyChange(index, 'value')}
              size="small"
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() => handleQuickReplyDelete(index)}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
        <Button
          variant="outlined"
          onClick={handleQuickReplyAdd}
          size="small"
          sx={{ mt: 1 }}
        >
          Add Quick Reply
        </Button>
      </Box>

      {onDelete && (
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          sx={{ mt: 2 }}
        >
          Remove Chat Configuration
        </Button>
      )}
    </Box>
  );
};

ChatConfig.propTypes = {
  config: PropTypes.shape({
    platform: PropTypes.oneOf(['whatsapp', 'messenger', 'telegram']),
    from_endpoint: PropTypes.string,
    priority: PropTypes.oneOf(['high', 'normal', 'low']),
    track_read: PropTypes.bool,
    quick_replies: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  }).isRequired,
  onConfigChange: PropTypes.func.isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  onEndpointChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default ChatConfig; 